// Intl-based formatters. All UI date/time/number rendering should go through
// these so switching the app locale later (Sprint 3 Phase 3) is a no-op here.

const memo = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>()

function getDateFmt(locale: string, opts: Intl.DateTimeFormatOptions) {
  const key = `date:${locale}:${JSON.stringify(opts)}`
  const cached = memo.get(key)
  if (cached) return cached as Intl.DateTimeFormat
  const f = new Intl.DateTimeFormat(locale, opts)
  memo.set(key, f)
  return f
}

function getNumberFmt(locale: string, opts: Intl.NumberFormatOptions) {
  const key = `num:${locale}:${JSON.stringify(opts)}`
  const cached = memo.get(key)
  if (cached) return cached as Intl.NumberFormat
  const f = new Intl.NumberFormat(locale, opts)
  memo.set(key, f)
  return f
}

export function formatDate(date: Date | string, locale = 'en'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return getDateFmt(locale, { dateStyle: 'medium' }).format(d)
}

export function formatDateTime(date: Date | string, locale = 'en'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return getDateFmt(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(d)
}

export function formatRelative(date: Date | string, locale = 'en'): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const diffMs = d.getTime() - Date.now()
  const minutes = Math.round(diffMs / 60_000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour')
  const days = Math.round(hours / 24)
  return rtf.format(days, 'day')
}

export function formatCurrency(amount: number, currency = 'USD', locale = 'en'): string {
  return getNumberFmt(locale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(amount)
}

export function formatNumber(n: number, locale = 'en'): string {
  return getNumberFmt(locale, {}).format(n)
}

export function formatPercent(n: number, locale = 'en'): string {
  return getNumberFmt(locale, { style: 'percent', maximumFractionDigits: 1 }).format(n)
}

export function formatDuration(ms: number): string {
  if (ms < 0) return '—'
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}h ${m}m`
  if (m > 0) return `${m}m ${s}s`
  return `${s}s`
}

export function formatCountdown(ms: number): string {
  const abs = Math.abs(ms)
  const totalSec = Math.floor(abs / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  const sign = ms < 0 ? '-' : ''
  const hh = String(h).padStart(2, '0')
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return `${sign}${hh}:${mm}:${ss}`
}
